import { useState, useEffect } from "react";
import { getAccessToken } from "../../services/authApi";
import { getUserDetails } from "../../services/userApi";
import { fieldReader, fieldWriter } from "../../services/loanApi";
import { LOAN_EDIT_LOCK_FIELD_IDS, isLoanEditLocked } from "../../utils/loanEditLock";
import Modal from "../shared/Modal";
import Loading from "../layout/Loading";
import { info, error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";
import { generateFilledPDF } from "../../services/documentApi";
import guidelineexception from "../../data/guideline_exception.json";
import { checkValidationRequired } from "../../utils/validationRedirect";
import { isWholesaleChannel } from "../../utils/channelUtils";
import GuidelineExceptionValidate from "../validation/GuidelineExceptionValidate";
import { markGuidelineExceptionValidationRedirect } from "../../utils/guidelineExceptionValidationRoute";
import { shouldRenderGuidelineExceptionValidation } from "../../utils/guidelineExceptionValidationRoute";
import { getExistingDocumentId, createDocument, getAttachmentUploadUrl, uploadAttachmentBlob, assignAttachmentToDocument } from "../../services/documentApi";
import "../../common.css";
import { getOrgConfig } from "../../utils/organizationConfigUtil";
import { EXCEPTION_LOAN_CHANNELS } from "../../config/organizations";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const guidelineExceptionValidationExternalPageID = process.env.REACT_APP_VALIDATION_GUIDELINE_EXCEPTION_EXTERNAL;
const guidelineExceptionValidationMenuItem = {
    label: "Request Guideline Exception",
    displayName: "Request Guideline Exception",
    validationExtPageId: guidelineExceptionValidationExternalPageID,
    validationPage: "GuidelineExceptionValidation"
};
const GuidelineExceptionRequest = () => {
    const [renderValidation] = useState(shouldRenderGuidelineExceptionValidation);

    if (renderValidation) {
        return <GuidelineExceptionValidate />;
    }

    return <GuidelineExceptionRequestContent />;
};

const GuidelineExceptionRequestContent = () => {
    // State for loading and notifications
    const [loading, setLoading] = useState(true);
    const [showToast, setShowToast] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorTitle, setErrorTitle] = useState("Submission Error");
    const [loanId, setLoanId] = useState(null);
    const [loanNumber, setLoanNumber] = useState(null);
    const [token, setToken] = useState(null);
    const [documentationType, setDocumentationType] = useState("Guideline");
    const [fieldsJson, setFieldsJson] = useState(guidelineexception);
    const [exceptionStatus, setExceptionStatus] = useState("");
    const [exceptionDate, setExceptionDate] = useState("");
    const [statusChecked, setStatusChecked] = useState(false);
    const [initialSubmitDate, setInitialSubmitDate] = useState("");
    // Read-only before submittal too, once disclosures are out (see utils/loanEditLock).
    const [editLocked, setEditLocked] = useState(false);
    const [channelType, setChannelType] = useState("");
    const [loanType, setLoanType] = useState("");

    // Main form state
    const [formData, setFormData] = useState({
        isAppraisalAvailable: "",
        guideline: "",
        explanation: "",
        compensatingFactors: {
            minReserves: false,
            minReservesValue: "",
            borrowerReserves: "",
            minFICO: false,
            minFICOValue: "",
            borrowerFICO: "",
            maxLTV: false,
            maxLTVValue: "",
            currentLTV: "",
            minDSCR: false,
            minDSCRValue: "",
            currentDSCR: "",
            yearsCleanHousing: false,
            yearsCleanHousingValue: "",
            otherFactors: ""
        },
        agree: false,
        loanProgram: "",
        productType: ""
    });

    //Email data
    const [emailData, setEmailData] = useState({
        authCode: "",
        name: "",
        phoneNumber: "",
        email: "",
        loanNumber: ""
    });

    useEffect(() => {
        const initialize = async () => {
            if (window.elli && window.elli.script && window.elli.script.guest) {
                window.elli.script.guest.create();

                await initializeAuthObject(); // Wait for status to load
                await fetchUserDetails(); // Wait for status to load
            } else {
                console.warn("elli.script.guest is not available.");
            }
            setLoading(false); // Only set to false after everything loads
        };
        initialize();
    }, []);

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async function (loanObject) {
                loanObject.all().then(async function (loan) {
                    setLoanId(loan.id);
                    setLoanNumber(loan.loanNumber);
                    setChannelType(loan.channel);
                    setDocumentationType("Guideline");

                    setFieldsJson(guidelineexception);
                    await getToken(false);

                    const tpo = loan?.tpo || {};
                    const submittedDate = tpo.initialSubmitDate || "";
                    setInitialSubmitDate(submittedDate);
                    if (submittedDate && submittedDate !== "//") {
                        // Delay to ensure DOM is rendered
                        setTimeout(() => {
                            makeFormReadOnly();
                        }, 100);
                    }

                    //Reading custom field value to show if exception is already submitted
                    const tpoAuthObject = await window.elli.script.getObject("auth");
                    const authCode = await tpoAuthObject.createAuthCode();
                    const reToken = await getAccessToken(authCode);

                    const redirectedToValidation = await validateGuidelineExceptionAccess(loan, reToken);
                    if (redirectedToValidation) {
                        return;
                    }

                    const allFieldIds = Array.from(new Set(["CX.EXC.PRE.SEC.DEC", "CX.EXC.PRE.REQ.DATE", "CX.LOAN.TYPE", ...LOAN_EDIT_LOCK_FIELD_IDS]));

                    const fieldResponse = await fieldReader(reToken, allFieldIds, loan.id);

                    const locked = isLoanEditLocked(fieldResponse);
                    setEditLocked(locked);
                    if (locked && !(submittedDate && submittedDate !== "//")) {
                        // Not yet submitted but disclosures are out - same read-only treatment
                        setTimeout(() => {
                            makeFormReadOnly();
                        }, 100);
                    }
                    setExceptionStatus(fieldResponse["CX.EXC.PRE.SEC.DEC"]);
                    setExceptionDate(fieldResponse["CX.EXC.PRE.REQ.DATE"]);
                    setLoanType(fieldResponse["CX.LOAN.TYPE"]);
                    setStatusChecked(true);
                    info("Guideline exception page loaded", { loanid: loan.id || "N/A" });
                });
            });
        } catch (error) {
            console.error("Auth block Error:", error);
            logAppInsightError("Guideline Exception Error: InitializeAuthObject Error", { error, location: "initializeAuthObject", context: "Failed to load loan information" });
            setErrorMessage("Failed to load loan information");
            setShowErrorToast(true);
        }
    };

    const validateGuidelineExceptionAccess = async (loan, accessToken) => {
        if (!isWholesaleChannel(loan?.channel) || !guidelineExceptionValidationExternalPageID) {
            return false;
        }

        const needsValidation = await checkValidationRequired(
            guidelineExceptionValidationMenuItem,
            loan.id,
            {},
            accessToken
        );

        if (!needsValidation) {
            return false;
        }

        const tpoAppObject = await window.elli.script.getObject("tpoApplication");
        markGuidelineExceptionValidationRedirect();

        await tpoAppObject.pageRedirect("ExternalLoanPage", {
            id: loan.id,
            extpageid: guidelineExceptionValidationExternalPageID,
            pagename: "GuidelineExceptionValidation"
        });

        return true;
    };

    const fetchUserDetails = async () => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const user = await tpoAuthObject.getUser();
            info("ExternalUserDetails loaded", { component: "Guideline exception", userid: user.id });
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);

            if (user && token) {
                const userData = await getUserDetails(token, user.id);
                const orgId = userData?.organization?.entityId;
                setEmailData(prev => ({
                    ...prev,
                    name: `${userData?.firstName ?? ""} ${userData?.lastName ?? ""}`,
                    phoneNumber: userData?.cellPhone ?? "",
                    email: userData?.email ?? ""
                }));
            }
        } catch (error) {
            const errorCode = logError("USER_FETCH_ERROR");
            logAppInsightError(`${errorCode} - fetching external user details.`, {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - fetching external user details.`,
                component: "Guideline exception"
            });
        }
    };

    const makeFormReadOnly = () => {
        const selectors = [
            "input:not([type=hidden]):not([readonly]):not([data-force-edit])",
            "textarea:not([readonly]):not([data-force-edit])",
            "select:not([disabled]):not([data-force-edit])",
            "input[type=checkbox]:not([disabled]):not([data-force-edit])",
            "button:not([data-allow])"
        ];

        const elements = document.querySelectorAll(selectors.join(", "));

        elements.forEach(el => {
            el.setAttribute("disabled", true);

            el.classList.add("opacity-60", "cursor-not-allowed");
        });
    };

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);

        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
        } catch (err) {
            console.error("Token fetch failed", err);
            logAppInsightError("Guideline Exception Error: getToken Error", { error: err, location: "getToken", context: "Failed to fetch token" });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = e => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith("compensatingFactors.")) {
            // Handle compensating factors fields
            const fieldPath = name.split(".");
            setFormData(prev => ({
                ...prev,
                compensatingFactors: {
                    ...prev.compensatingFactors,
                    [fieldPath[1]]: type === "checkbox" ? checked : value
                }
            }));
        } else {
            // Handle regular fields
            setFormData(prev => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value
            }));
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            // Validate compensating factors
            const validationErrors = [];

            // // Check if any compensating factor is checked but missing values
            // if (formData.compensatingFactors.minReserves &&
            //     (!formData.compensatingFactors.minReservesValue || !formData.compensatingFactors.borrowerReserves)) {
            //     validationErrors.push("Please enter both Min Reserves and Borrower's Reserves values");
            // }

            // if (formData.compensatingFactors.minFICO &&
            //     (!formData.compensatingFactors.minFICOValue || !formData.compensatingFactors.borrowerFICO)) {
            //     validationErrors.push("Please enter both Min FICO and Borrower's FICO values");
            // }

            // if (formData.compensatingFactors.maxLTV &&
            //     (!formData.compensatingFactors.maxLTVValue || !formData.compensatingFactors.currentLTV)) {
            //     validationErrors.push("Please enter both Max LTV and Current LTV values");
            // }

            // if (formData.compensatingFactors.minDSCR &&
            //     (!formData.compensatingFactors.minDSCRValue || !formData.compensatingFactors.currentDSCR)) {
            //     validationErrors.push("Please enter both Min DSCR and Current DSCR values");
            // }

            // if (formData.compensatingFactors.yearsCleanHousing &&
            //     !formData.compensatingFactors.yearsCleanHousingValue) {
            //     validationErrors.push("Please enter Years of Clean Housing value");
            // }

            if (!formData.isAppraisalAvailable) {
                validationErrors.push("Please Select Is Appraisal Available?");
            }
            if (!formData.guideline) {
                validationErrors.push("Please Enter the Exact Guideline that needs an exception:");
            }
            if (!formData.explanation) {
                validationErrors.push("Please Enter Explain why the borrower cannot meet the guideline, what they can meet, etc");
            }
            if (!formData.loanProgram) {
                validationErrors.push("Please Select Loan Program");
            }
            if (!formData.productType) {
                validationErrors.push("Please Select Product Type");
            }

            const compensatingFactors = Object.fromEntries(Object.entries(formData.compensatingFactors).filter(([key, value]) => typeof value === "boolean"));
            if (!compensatingFactors || Object.entries(compensatingFactors).filter(([key, value]) => value).length < 2) {
                validationErrors.push("Please Choose At least 2 Compensating Factors");
            }

            if (validationErrors.length > 0) {
                setErrorMessage(validationErrors.join("\n"));
                setShowErrorToast(true);
                setLoading(false);
                return;
            }

            // Combine the guideline and explanation with separator
            const reqSummaryValueRaw = `${formData.guideline}||${formData.explanation}`;
            const reqSummaryValue = reqSummaryValueRaw.replace(/\|\|/g, "||\n");

            // Combine compensating factors in the requested format
            const compFactorsValuesRaw = [
                formData.compensatingFactors.minReserves
                    ? `Min Reserves: ${formData.compensatingFactors.minReserves}, ${formData.compensatingFactors.minReservesValue}, ${formData.compensatingFactors.borrowerReserves}`
                    : "",
                formData.compensatingFactors.minFICO
                    ? `Min FICO: ${formData.compensatingFactors.minFICO}, ${formData.compensatingFactors.minFICOValue}, ${formData.compensatingFactors.borrowerFICO}`
                    : "",
                formData.compensatingFactors.maxLTV
                    ? `Max LTV: ${formData.compensatingFactors.maxLTV}, ${formData.compensatingFactors.maxLTVValue}, ${formData.compensatingFactors.currentLTV}`
                    : "",
                formData.compensatingFactors.minDSCR
                    ? `Min DSCR: ${formData.compensatingFactors.minDSCR}, ${formData.compensatingFactors.minDSCRValue}, ${formData.compensatingFactors.currentDSCR}`
                    : "",
                formData.compensatingFactors.yearsCleanHousing
                    ? `Years Clean Housing: ${formData.compensatingFactors.yearsCleanHousing}, ${formData.compensatingFactors.yearsCleanHousingValue}`
                    : "",
                formData.compensatingFactors.otherFactors ? `Other Factors: ${formData.compensatingFactors.otherFactors}` : ""
            ]
                .filter(Boolean)
                .join("||");
            const compFactorsValues = compFactorsValuesRaw.replace(/\|\|/g, "||\n");
            const fieldData = [
                {
                    id: "CX.EXC.PRE.APPRAISAL.AVAIL",
                    value: formData.isAppraisalAvailable
                },
                {
                    id: "CX.EXC.PRE.REQ.SUMMARY",
                    value: reqSummaryValue
                },
                {
                    id: "CX.EXC.PRE.COMP.FACTORS",
                    value: compFactorsValues
                },
                // {
                //     id: "CX.EXC.PRE.SEC.DEC", //"Pre-Submission Secondary Decision"
                //     value: "Requested"
                // },
                {
                    id: "CX.EXC.PRE.REQ.DATE",
                    value: new Date().toISOString().split("T")[0] // "YYYY-MM-DD"
                },
                {
                    id: "CX.EXC.PRE.REQ.LOANPROGRAM",
                    value: formData.loanProgram
                },
                {
                    id: "CX.EXC.PRE.REQ.PRODUCTTYPE",
                    value: formData.productType
                }
            ];

            //updating the custom field values
            info("Guideline exception added", { loanid: loanId });
            await fieldWriter(token, loanId, fieldData);

            //confirm for reading
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const tokens = await getAccessToken(authCode);

            const allFieldIds = Array.from(
                new Set(["CX.EXC.PRE.REQ.SUMMARY", "CX.EXC.PRE.COMP.FACTORS", "CX.EXC.PRE.SEC.DEC", "CX.EXC.PRE.APPRAISAL.AVAIL", "CX.EXC.PRE.REQ.DATE"])
            );

            const fieldResponse = await fieldReader(tokens, allFieldIds, loanId);

            //generating PDF api call

            await SavePDF();
            await refreshFields();
            //Send email for exception only for corrrespondent channel
            if (channelType.toLowerCase() === "correspondent") {
                const reauthCode = await tpoAuthObject.createAuthCode();
                const url = `${API_DOMAIN}/api/exception-request/exceptionrequestemail`;
                try {
                    const formDataWithAuth = {
                        authCode: reauthCode,
                        name: emailData.name,
                        email: emailData.email,
                        phoneNumber: emailData.phoneNumber,
                        loanNumber: loanNumber,

                        requestDetails: {
                            isAppraisalAvailable: formData.isAppraisalAvailable === "Yes" ? true : false,
                            requestSummary: reqSummaryValue,
                            compensatingFactors: compFactorsValues,
                            requestDate: new Date().toISOString(),
                            loanProgram: formData.loanProgram,
                            productType: formData.productType
                        }
                    };
                    const response = await fetch(url, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(formDataWithAuth)
                    });
                    if (!response.ok) {
                        logAppInsightError("Guideline Exception Error: Email API Failed", {
                            location: "handleSubmit",
                            context: "exceptionrequestemail returned non-success status",
                            loanId,
                            channelType,
                            status: response.status,
                            statusText: response.statusText
                        });
                    } else {
                        info("Guideline Exception: Email Sent", { component: "Guideline Exception", loanId, channelType });
                    }
                } catch (error) {
                    logAppInsightError("Guideline Exception Error: Email Send Error", { error, location: "handleSubmit", context: "Failed to send email", loanId, channelType });
                }
            }

            setShowToast(true);
            setTimeout(() => setShowToast(false), 10000);
        } catch (error) {
            logAppInsightError("Guideline Exception Error: handleSubmit Error", { error, location: "handleSubmit", context: "Failed to submit form", formData });
            setErrorMessage(error.message || "Failed to submit form");
            setShowErrorToast(true);
        } finally {
            setLoading(false);
        }
    };

    const processFields = async (fieldsJson, formValues) => {
        try {
            // List of fields to skip from calling fieldReader
            const manualFieldIds = new Set([
                "isAppraisalAvailable",
                "guideline",
                "explanation",
                "minReservesValue",
                "borrowerReserves",
                "minReserves",
                "minFICOValue",
                "borrowerFICO",
                "minFICO",
                "maxLTVValue",
                "currentLTV",
                "maxLTV",
                "property",
                "currentDSCR",
                "minDSCR",
                "yearsCleanHousingValue",
                "yearsCleanHousing",
                "otherFactors",
                "agree",
                "minDSCRValue",
                ""
            ]);

            // Step 1: Filter out IDs that need to be manually set
            const apiFieldIds = fieldsJson.map(f => f.id).filter(id => !manualFieldIds.has(id));

            // Step 2: Get token
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const newtoken = await getAccessToken(authCode);

            // Step 3: Call fieldReader only for non-manual fields
            const fieldResponse = await fieldReader(newtoken, apiFieldIds, loanId);

            // Step 4: Merge values (API + manually from formValues)
            const updatedFields = fieldsJson.map(f => {
                const rawId = f.id;

                let value;

                if (manualFieldIds.has(rawId)) {
                    // 🔍 Get from either root or compensatingFactors
                    value = formValues[rawId] ?? formValues.compensatingFactors?.[rawId] ?? "";
                } else {
                    value = fieldResponse[rawId] || "";
                }
                // 2. Always convert the value to string
                const stringValue = value === true ? "true" : value === false ? "false" : String(value);
                return {
                    id: rawId,
                    value: stringValue,
                    PDFFieldName: f.PDFFieldName,
                    PDFFieldType: f.PDFFieldType,
                    PDFGroupField: f.PDFGroupField,
                    PDFGroupOrder: f.PDFGroupOrder,
                    Separator: f.Separator
                };
            });
            return updatedFields;
        } catch (error) {
            console.error("Processing fields failed", error);
            logAppInsightError("Guideline Exception Error: processFields Error", { error, location: "processFields", context: "Failed to process fields", fieldsJson, formValues });
            throw error; // Let SavePDF handle it
        }
    };

    const SavePDF = async () => {
        const updatedFields = await processFields(fieldsJson, formData);

        const filteredFields = updatedFields.filter(f => f.PDFFieldName);

        try {
            const pdfBlob = await generateFilledPDF(filteredFields, documentationType);

            const documentTitle = "Guideline Exception Request";
            const fileName = "Exception Form - Guideline.pdf";

            // Step 1: Retrieve Documents
            let documentId = await getExistingDocumentId(token, loanId, documentTitle);

            // Step 2: Create a Document
            if (!documentId) {
                documentId = await createDocument(token, loanId, documentTitle);
            }

            // Step 3: Get URL to Upload Attachment to eFolder
            const uploadInfo = await getAttachmentUploadUrl(token, loanId, fileName, pdfBlob.size, "");

            // Step 4: Upload Attachment
            await uploadAttachmentBlob(uploadInfo.uploadUrl, uploadInfo.authorizationHeader, pdfBlob, "");

            // Step 5: Assign Attachments
            await assignAttachmentToDocument(token, loanId, documentId, uploadInfo.attachmentId);
        } catch (error) {
            const message = error?.message || "Failed to submit the form. Please try again.";
            console.error("PDF generation failed", error);
            logAppInsightError("Guideline Exception Error: SavePDF Error", {
                error,
                location: "SavePDF",
                context: "Failed to generate or upload PDF",
                fields: filteredFields,
                documentationType
            });
            setErrorMessage(message);
            setShowErrorToast(true);
        }
    };
    const refreshFields = async () => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);

            const allFieldIds = ["CX.EXC.PRE.SEC.DEC", "CX.EXC.PRE.REQ.DATE"];
            const fieldResponse = await fieldReader(token, allFieldIds, loanId);

            setExceptionStatus(fieldResponse["CX.EXC.PRE.SEC.DEC"]);
            setExceptionDate(fieldResponse["CX.EXC.PRE.REQ.DATE"]);
        } catch (error) {
            console.error("Refresh fields failed", error);
            logAppInsightError("Guideline Exception Error: refreshFields Error", { error, location: "refreshFields", context: "Failed to refresh exception status/date" });
        }
    };
    const isAlreadySubmitted = exceptionStatus.toLowerCase() !== "requested" && exceptionDate !== "//";
    if (!statusChecked) {
        return <Loading />;
    }

    /*
     * Per-company, from src/config/organizations.js. The rate-sheet variants
     * differ by brand - "DSCR 1-4 units (Accelerator)" for ChampsTPO,
     * "(Breezy)" for SkyTPO - and each carries products the other does not,
     * so the whole list comes from the org rather than being filtered here.
     */
    const loanPrograms = getOrgConfig().exceptionLoanPrograms ?? [];

    /*
     * `loanType` is CX.LOAN.TYPE - "Business" or "Consumer". A program marked
     * BOTH is offered under either. An entry with no channel at all is treated
     * the same way rather than disappearing.
     */
    const isProgramForLoanType = (program) =>
        !program.key ||
        program.key === EXCEPTION_LOAN_CHANNELS.BOTH ||
        program.key === loanType;

    const productTypes = [
        { value: "ARM", label: "ARM" },
        { value: "Fixed", label: "Fixed" },
        { value: "I/O", label: "I/O" }
    ];

    return (
        <>
            {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-400 block-ui-overlay block-ui-container">
                    <div className="block-ui-message-container">
                        <div className="block-ui-message">Loading...</div>
                    </div>
                </div>
            )}

            {showToast && (
                <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-green-100 border-l-4 border-green-600 text-green-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                    <div className="text-2xl">✅</div>
                    <div className="flex-1">
                        <div className="font-semibold mb-1">Success</div>
                        <div className="text-sm leading-relaxed break-words">Form saved successfully</div>
                    </div>
                </div>
            )}

            <Modal title={errorTitle} message={errorMessage} isOpen={showErrorToast} onClose={() => setShowErrorToast(false)} type="error" />

            <div className="space-y-1 p-25px">
                <div className="bg-[var(--white)] rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">Guideline Exception Request</h2>
                    {isAlreadySubmitted ? (
                        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                            <div className="section-header">Exception Status</div>

                            <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl p-4 overflow-auto">
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                                    <p className="form-label mb-1">
                                        You have already submitted. The current exception status is{" "}
                                        <span
                                            className={`font-bold ${exceptionStatus.toLowerCase() === ""
                                                ? "text-orange-600"
                                                : exceptionStatus.toLowerCase() === "approved"
                                                    ? "text-green-600"
                                                    : exceptionStatus.toLowerCase() === "denied"
                                                        ? "text-red-600"
                                                        : exceptionStatus.toLowerCase() === "conditioned"
                                                            ? "text-yellow-600"
                                                            : "text-gray-600"
                                                }`}
                                        >
                                            {exceptionStatus.toLowerCase() === "" ? "Submitted" : exceptionStatus.charAt(0).toUpperCase() + exceptionStatus.slice(1).toLowerCase()}
                                        </span>
                                        .
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                                <div className="section-header">Guideline Details</div>

                                <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl p-4 overflow-auto">
                                    <p className="form-label mb-1">
                                        This will submit an Exception Request Form to {getOrgConfig()?.shortName}. You may be asked for any additional relevant documentation such as Asset Statements,
                                        Income Documentation, Appraisal, Listing, Letter of Explanations, etc., to support the exception request and compensating factors. If
                                        exception is approved, loan may be subject to exception-based pricing.
                                    </p>

                                    <div className="mb-4">
                                        <label className="form-label mb-1">
                                            Is Appraisal Available? <span className="error_message">*</span>
                                        </label>
                                        <div className="flex gap-6">
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    name="isAppraisalAvailable"
                                                    value="Yes"
                                                    checked={formData.isAppraisalAvailable === "Yes"}
                                                    onChange={handleChange}
                                                    className="form-radio align-middle"
                                                />
                                                <span className="text-sm leading-tight alignment">Yes</span>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    name="isAppraisalAvailable"
                                                    value="No"
                                                    checked={formData.isAppraisalAvailable === "No"}
                                                    onChange={handleChange}
                                                    className="form-radio align-middle"
                                                />
                                                <span className="text-sm leading-tight alignment">No</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label mb-1">
                                            Enter the Exact Guideline that needs an exception: <span className="error_message">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="guideline"
                                            value={formData.guideline}
                                            onChange={handleChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2"
                                        />
                                    </div>

                                    <div className="mb-6">
                                        <label className="form-label mb-1">
                                            Explain why the borrower cannot meet the guideline, what they can meet, etc. <span className="error_message">*</span>
                                        </label>
                                        <textarea
                                            name="explanation"
                                            value={formData.explanation}
                                            onChange={handleChange}
                                            rows={3}
                                            className="w-full border border-gray-300 rounded px-3 py-2 resize-none"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                                        <div className="flex items-center">
                                            <label className="form-label mb-1 w-48 alignment-checkbox">
                                                Loan Program: <span className="error_message">*</span>
                                            </label>
                                            <select
                                                id="loanProgram"
                                                name="loanProgram"
                                                className="form-input w-full text-sm border rounded p-1"
                                                defaultValue={formData.loanProgram}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Loan Program</option>
                                                {loanPrograms.filter(isProgramForLoanType).map(item => (
                                                    <option key={item.value} value={item.value}>
                                                        {item.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex items-center">
                                            <label className="form-label mb-1 w-48 alignment-checkbox">
                                                Product Type: <span className="error_message">*</span>
                                            </label>
                                            <select
                                                id="productType"
                                                name="productType"
                                                className="form-input w-full text-sm border rounded p-1"
                                                defaultValue={formData.productType}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Product Type</option>
                                                {productTypes.map(item => (
                                                    <option key={item.value} value={item.value}>
                                                        {item.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                                <div className="section-header">
                                    Compensating Factors: ‡<span className="error_message">*</span> (Please choose at least 2)
                                </div>
                                <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl p-4 overflow-auto">
                                    <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                                        {/* Min Reserves */}
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                name="compensatingFactors.minReserves"
                                                checked={formData.compensatingFactors.minReserves}
                                                onChange={handleChange}
                                                className="form-checkbox text-blue-600" // Fixed size for consistency
                                            />
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Min Reserves Required (mos):</label>

                                            <input
                                                type="text"
                                                name="compensatingFactors.minReservesValue"
                                                value={formData.compensatingFactors.minReservesValue}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Borrower's Reserves (mos):</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.borrowerReserves"
                                                value={formData.compensatingFactors.borrowerReserves}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1 "
                                            />
                                        </div>

                                        {/* Min FICO */}
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                name="compensatingFactors.minFICO"
                                                checked={formData.compensatingFactors.minFICO}
                                                onChange={handleChange}
                                                className="form-checkbox text-blue-600"
                                            />
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Min FICO:</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.minFICOValue"
                                                value={formData.compensatingFactors.minFICOValue}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Borrower's FICO:</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.borrowerFICO"
                                                value={formData.compensatingFactors.borrowerFICO}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>

                                        {/* Max LTV */}
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                name="compensatingFactors.maxLTV"
                                                checked={formData.compensatingFactors.maxLTV}
                                                onChange={handleChange}
                                                className="form-checkbox text-blue-600"
                                            />
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Max LTV:</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.maxLTVValue"
                                                value={formData.compensatingFactors.maxLTVValue}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Current LTV:</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.currentLTV"
                                                value={formData.compensatingFactors.currentLTV}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>

                                        {/* Min DSCR */}
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                name="compensatingFactors.minDSCR"
                                                checked={formData.compensatingFactors.minDSCR}
                                                onChange={handleChange}
                                                className="form-checkbox text-blue-600"
                                            />
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Min DSCR:</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.minDSCRValue"
                                                value={formData.compensatingFactors.minDSCRValue}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Current DSCR:</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.currentDSCR"
                                                value={formData.compensatingFactors.currentDSCR}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>

                                        {/* Years of Clean Housing */}
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                name="compensatingFactors.yearsCleanHousing"
                                                checked={formData.compensatingFactors.yearsCleanHousing}
                                                onChange={handleChange}
                                                className="form-checkbox text-blue-600"
                                            />
                                            <label className="form-label mb-1 w-48 alignment-checkbox">Years of Clean Housing:</label>
                                            <input
                                                type="text"
                                                name="compensatingFactors.yearsCleanHousingValue"
                                                value={formData.compensatingFactors.yearsCleanHousingValue}
                                                onChange={handleChange}
                                                className="w-32 border border-gray-300 rounded px-2 py-1"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4 alignment-checkbox">
                                        <label className="form-label mb-1">Other Compensating Factors: ‡</label>
                                        <textarea
                                            name="compensatingFactors.otherFactors"
                                            value={formData.compensatingFactors.otherFactors}
                                            onChange={handleChange}
                                            rows={3}
                                            className="w-full border border-gray-300 rounded px-3 py-2 resize-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            <p className="form-label mb-1">‡ Compensating factors are loan parameters that lower exception risk.</p>

                            <p className="form-label mb-1">
                                By submitting this exception request, broker certifies that the information provided is accurate and complete, that no critical information has been
                                withheld, and that any changes to the information provided could change the decision upon re-review. In addition to any required documentation,
                                please provide any additional documentation to support the exception request. If exception is approved, any change to the parameters listed on this
                                approval requires the loan be returned to credit risk for review and decision could change.
                            </p>

                            <div className="flex justify-end">
                                <button
                                    {...((!initialSubmitDate || initialSubmitDate === "//") && !editLocked ? { onClick: handleSubmit } : {})}
                                    className={`btn-custom rounded transition-colors duration-200 ${(initialSubmitDate && initialSubmitDate !== "//") || editLocked ? "bg-gray-400 text-white cursor-not-allowed" : "text-[#FFFFFF] bg-[#2080C3] hover:text-[#E6EEF8] hover:bg-[#5DA5D9] active:bg-white"}`}
                                    disabled={(initialSubmitDate && initialSubmitDate !== "//") || editLocked}
                                >
                                    I Agree, Send Request
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default GuidelineExceptionRequest;
